import {
  Controller,
  FetchOptions,
  FetchPageOptions,
  AnalyticsStore,
  PageStore,
  codeSplit
} from "bernie-core";
import { PageData } from "bernie-http";
import {
  ExtendedContextStore,
  FlexBffTemplateResponse,
  Template
} from "typings/flexFramework/FlexDefinitions";
import { TemplateStore } from "stores/TemplateStore";
import { FlexViewModelStore } from "stores/flexViewModel/FlexViewModelStore";
import { BlossomCodeSplitterFactory } from "components/flexFramework/CodeSplitter/BlossomCodeSplitterFactory";
import { BlossomCodeSplitter } from "src/components/flexFramework/CodeSplitter/BlossomCodeSplitter";
import { moduleDefinitionsMap } from "components/flexFramework/ModuleDefinitions/moduleDefinitionMap";

interface FlexFetchPageOptions extends FetchPageOptions {
  context: ExtendedContextStore;
}

interface WindowWithState extends Window {
  __STATE__: {
    templateStore: TemplateStore;
  };
}

/**
 * New controller that currently only serves Hotel-Reviews type pages.
 * Currently work in progress in order to work towards the BEXG Tech Strategy.
 * More info here: More info: https://confluence.expedia.biz/display/~mhaddock/Flex%3A+full+adoption+of+Tech+Strategy
 */
export class FlexController implements Controller {
  public pageId: string; // x-page-id is set in the middleware
  // TODO: Need to find a better way to switch between controllers so we can test other pages using template endpoint
  // Currently only supports Hotel-Review pages based on: https://github.expedia.biz/Flex/ewe-link-service/blob/master/src/main/resources/com/expedia/www/link/config/LinkServiceConfig.json#L2518-L2639
  public path =
    "/*h:id.:name(Hotel-Reviews|Opiniones-Hotel|Comptes-Rendus-Hotel|Hotel-Bewertung|Recensioner|Evaluations-Hotel|hotellitiedot|Hotel-Anmeldelser|Giudizi-Hotel|Vurderinger-Hoteller|Hotelrecensies|Avaliacoes-Hotel|Hotel-Pagsusuri|Danh-gia-khach-san)";
  public routeName = "flex-FlexPage";
  public bundles = ["flex-FlexPage"];
  public exact = false;
  public component = codeSplit(async () => {
    const routeComponent = import(/* webpackChunkName: "flex-FlexPage" */ "views/FlexPage") as any;
    let template: Template | undefined;

    // Client-side only
    if (typeof window !== "undefined") {
      const serializedState = (window as WindowWithState).__STATE__;
      template =
        serializedState && serializedState.templateStore
          ? serializedState.templateStore.template
          : undefined;
    }

    if (template) {
      await BlossomCodeSplitterFactory.get({ template }).codeSplitAllReactComponents();
    } else {
      await new BlossomCodeSplitter(moduleDefinitionsMap, []).codeSplitAllReactComponents();
    }

    return routeComponent;
  });

  public fetch(options: FetchOptions): Promise<any> {
    const { stores } = options;
    const flexViewModelStore = stores && stores.get<FlexViewModelStore>("flexViewModel");
    const pageStore = stores && stores.get<PageStore>("page");
    const analyticsStore = stores && stores.get<AnalyticsStore>("analytics");
    const contextStore = stores && stores.get<ExtendedContextStore>("context");
    const templateStore = stores && stores.get<TemplateStore>("templateStore");

    // We get it from the middleware which hijacks the contextStore
    const flexBffResponse = contextStore.viewModelResponse as FlexBffTemplateResponse;
    const footer: string = contextStore.footer;
    delete contextStore.footer;
    delete contextStore.viewModelResponse;

    flexViewModelStore.setupFlexViewModelStore(
      flexBffResponse,
      pageStore,
      contextStore,
      analyticsStore,
      footer
    );

    templateStore.setup(flexBffResponse);

    return Promise.resolve(templateStore.fetchAndStoreBreadcrumbsData(contextStore));
  }

  public fetchPageData(options: FlexFetchPageOptions): Promise<PageData> {
    const bffResponse = options.context.viewModelResponse as FlexBffTemplateResponse;
    const additionalScripts = BlossomCodeSplitterFactory.get({
      template: bffResponse.template
    })
      .getWebpackReactComponents()
      .map(component => component && component.webpackChunkName);

    const seoMetaData = import(/* webpackChunkName: "SeoUtils" */ "utils/seo/SeoUtils").then(
      ({ default: _ }) => {
        return (
          _.buildPageDataWithSeoMetaDataFromFlexBffTemplate(bffResponse) || {
            title: bffResponse.template.title
          }
        );
      }
    );

    return Promise.resolve(seoMetaData).then(m => {
      return {
        ...m,
        assetsToIncludeFilter: (assetKey: string) => additionalScripts.includes(assetKey)
      };
    });
  }
}
